/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useState } from 'react';

import { css, jsx } from '@compiled/react';

import DiagramSymbolMindMapIcon from '@atlaskit/icon-lab/core/diagram-symbol-mind-map';
import StickerIcon from '@atlaskit/icon-lab/core/sticker';
import ShapesIcon from '@atlaskit/icon/core/shapes';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { token } from '@atlaskit/tokens';

import {
	PROGRESS_BAR_TEST_ID_SUFFIX,
	SLIDE_MORPH_TEST_ID_SUFFIX,
	STATIC_PROGRESS_VALUE,
} from './constants';
import ProgressBar from './progress-bar';
import SlideMorph from './slide-morph';
import type { LoadingOverlayProps } from './types';

const containerStyles = css({
	position: 'absolute',
	inset: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: token('elevation.surface.sunken'),
});

const innerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: token('space.250'),
});

const progressBarWrapperStyles = css({
	width: '172px',
});

const defaultIcons = [WhiteboardIcon, DiagramSymbolMindMapIcon, ShapesIcon, StickerIcon];

export default function LoadingOverlay({
	animationDisabled = false,
	icons,
	testId,
}: LoadingOverlayProps): JSX.Element {
	const [progress, setProgress] = useState(animationDisabled ? STATIC_PROGRESS_VALUE : 0);

	useEffect(() => {
		if (animationDisabled) {
			return;
		}

		const interval = setInterval(() => {
			setProgress((prev) => {
				if (prev < 10) {
					return prev + 10;
				} else if (prev < 60) {
					return prev + 35;
				} else if (prev < 90) {
					return prev + 10;
				} else {
					clearInterval(interval);
					return prev;
				}
			});
		}, 1000);

		return () => clearInterval(interval);
	}, [animationDisabled]);

	return (
		<div css={containerStyles} data-testid={testId}>
			<div css={innerStyles}>
				<SlideMorph
					animationDisabled={animationDisabled}
					icons={icons ?? defaultIcons}
					testId={testId ? `${testId}--${SLIDE_MORPH_TEST_ID_SUFFIX}` : undefined}
					cycleDirection="right"
				/>
				<div css={progressBarWrapperStyles}>
					<ProgressBar
						ariaLabel="Loading"
						testId={testId ? `${testId}--${PROGRESS_BAR_TEST_ID_SUFFIX}` : undefined}
						value={progress}
					/>
				</div>
			</div>
		</div>
	);
}
